// Die Planetenoberfläche — eine Phaser-Szene im Ausschnitt des Kolonie-Panels.
//
// Dasselbe Muster wie Karte und Technologiebaum, und aus demselben Grund: Text
// und Listen im DOM, ein Kachelfeld mit Anlagen auf Canvas. Und dieselben zwei
// Riegel — `setVisible` schaltet nur das Zeichnen ab, `setOpen` die Zeiger.
// Ohne den zweiten setzte ein Tipp auf die Welt eine Anlage, die niemand sah.
//
// **Sie rechnet nichts und entscheidet nichts.** Was eine Kachel trägt, steht
// in `sim/planets.ts`, ob dort gebaut werden darf, in `sim/colony.ts`; ein Tipp
// geht als Kachelkoordinate an die Verdrahtung, und die fragt das Panel. Eine
// Szene, die selbst baute, wäre die zweite Regelquelle.
//
// Die Uhr läuft in `update`, gezeichnet wird in `draw` — und `draw` weist
// nichts zu (FG-17).

import Phaser from 'phaser';

import { bus } from '../core/bus';
import { balancing, colonyBuildingById, goodById, planetById } from '../data/balancing';
import { iconTextureKey } from '../gfx/textures';
import { ensurePlanetTextures } from '../gfx/textures';
import { groundShade, planetTextureKey } from '../gfx/planet';
import {
  buildingAt, inArea, placeLocked, siteOk, type ColonyBuildingState, type ColonyState,
  type ColonyView, type Outpost,
} from '../sim/colony';
import { depositAt } from '../sim/planets';
import { store } from '../state/store';

/** Was die Szene nach außen meldet. Sie entscheidet nichts selbst. */
export interface PlanetCallbacks {
  onTile(x: number, y: number): void;
}

const NO_CALLBACKS: PlanetCallbacks = { onTile: () => {} };

/** Kantenlänge einer Kachel in Texeln — ein Icon ist 16 Raster breit. */
const TILE = 16;
/** Die Icons liegen als 64er-Texturen vor; ein Viertel davon ist genau ein Raster je Texel. */
const ICON_SCALE = TILE / 64;

/** Farben der Oberfläche. Sie stehen bei den Formen, wie bei Karte und Bauteilen. */
const BG = '#090c12';
const FARBE_AUSSEN = 0x05070b;
const FARBE_AUSWAHL = 0xffe98a;
const FARBE_FREI = 0x7ee06a;
const FARBE_HUNGER = 0xff6a5c;
const FARBE_STUFE = 0x5fe6d8;
const PLATTE: Record<string, number> = {
  hq: 0xd8d2c4, drill: 0x6a5a2a, make: 0x34465a, power: 0x2a5a52,
};

export class PlanetScene extends Phaser.Scene {
  static readonly KEY = 'planet';

  private gfx!: Phaser.GameObjects.Graphics;
  private label!: Phaser.GameObjects.Text;
  private sphere!: Phaser.GameObjects.Image;
  private callbacks: PlanetCallbacks = NO_CALLBACKS;
  private open = false;

  /** Die Icons der Anlagen — ein Pool, der je Bild umbelegt wird (FG-14). */
  private readonly icons: Phaser.GameObjects.Image[] = [];
  private view: ColonyView = { planet: '', selected: null, placing: '', moving: false };
  private starved: Record<string, boolean> = {};

  /** Der Zeichenmaßstab — in `update` gesetzt, in `draw` nur gelesen. */
  private zoom = 1;
  private ox = 0;
  private oy = 0;
  private hover: { x: number; y: number } | null = null;

  constructor() {
    super({ key: PlanetScene.KEY, active: false });
  }

  setCallbacks(callbacks: PlanetCallbacks): void {
    this.callbacks = callbacks;
  }

  /** Zeiger nur, solange der Reiter offen ist — derselbe Riegel wie bei der Karte. */
  setOpen(open: boolean): void {
    this.open = open;
    if (!open) {
      this.hover = null;
      this.label.setText('');
    }
  }

  /** Was das Panel gerade zeigt — Planet, Auswahl, Bauplan — und welche Bänder hungern. */
  setView(view: ColonyView, starved: Record<string, boolean>): void {
    this.view = view;
    this.starved = starved;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(BG);
    this.gfx = this.add.graphics().setDepth(0);
    this.sphere = this.add.image(0, 0, '__DEFAULT').setVisible(false).setDepth(0);
    this.label = this.add.text(6, 6, '', {
      fontFamily: '"Lucida Console", monospace', fontSize: '11px', color: '#cdc6b8',
      backgroundColor: 'rgba(9,12,18,0.75)', padding: { x: 4, y: 2 },
    }).setDepth(3).setScrollFactor(0);
    this.scene.setVisible(false);
    this.bindInput();
  }

  /** Den Zeichenbereich an den Panel-Ausschnitt binden. */
  setViewportRect(rect: DOMRect): void {
    this.cameras.main.setViewport(
      Math.round(rect.left), Math.round(rect.top),
      Math.round(rect.width), Math.round(rect.height),
    );
  }

  /**
   * Den Maßstab rechnen: das ganze Raster passt in den Ausschnitt.
   *
   * **Die Kachel ist eine ganze Zahl Bildpunkte breit**, nicht der Zoom: so
   * bleibt das Raster scharf, und es füllt den Ausschnitt trotzdem. Ein
   * ganzzahliger Zoom ließe bei einem Panel neben der Fläche die Hälfte leer —
   * 21 Kacheln zu 32 Bildpunkten passen in keinen Laptop-Ausschnitt. Der
   * Vertrag des ganzzahligen Zooms gilt der Welt, nicht diesem Panel
   * (`VIEW-001`), wie beim Technologiebaum.
   */
  override update(): void {
    if (!this.scene.isVisible()) return;
    const c = balancing().colony;
    const camera = this.cameras.main;
    const breite = c.gridW * TILE;
    const hoehe = c.gridH * TILE;
    const passt = Math.min(camera.width / breite, camera.height / hoehe);
    this.zoom = Math.max(4, Math.floor(TILE * passt)) / TILE;
    this.ox = Math.round((camera.width - breite * this.zoom) / 2);
    this.oy = Math.round((camera.height - hoehe * this.zoom) / 2);
    if (!store.save.colony.planets[this.view.planet]) ensurePlanetTextures(this);
    this.draw();
  }

  private draw(): void {
    const g = this.gfx;
    g.clear();
    const outpost = store.save.colony.planets[this.view.planet];
    const planet = planetById(this.view.planet);
    if (!planet) return;
    if (!outpost) {
      this.drawSphere();
      return;
    }
    this.sphere.setVisible(false);
    this.drawGround(g, store.save.colony);
    this.drawBuildings(g, outpost);
    this.drawMarks(g, store.save.colony, outpost);
  }

  /** Ein unbesiedelter Planet: die Kugel, wie sie im Feld steht. */
  private drawSphere(): void {
    for (const icon of this.icons) icon.setVisible(false);
    const key = planetTextureKey(this.view.planet);
    if (!this.textures.exists(key)) return;
    const camera = this.cameras.main;
    const quelle = this.textures.get(key).getSourceImage() as { width: number };
    const masz = Math.min(1, (Math.min(camera.width, camera.height) * 0.8) / quelle.width);
    this.sphere.setTexture(key).setVisible(true)
      .setPosition(camera.width / 2, camera.height / 2).setScale(masz);
  }

  private tileX(x: number): number { return this.ox + x * TILE * this.zoom; }
  private tileY(y: number): number { return this.oy + y * TILE * this.zoom; }

  /** Boden, Gebietsgrenze und Vorkommen. */
  private drawGround(g: Phaser.GameObjects.Graphics, colony: ColonyState): void {
    const c = balancing().colony;
    const planet = planetById(this.view.planet);
    if (!planet) return;
    const s = TILE * this.zoom;
    for (let y = 0; y < c.gridH; y++) {
      for (let x = 0; x < c.gridW; x++) {
        const px = this.tileX(x);
        const py = this.tileY(y);
        g.fillStyle(groundShade(planet, x, y), 1);
        g.fillRect(px, py, s, s);
        this.drawDeposit(g, x, y, px, py, s);
        if (!inArea(colony, this.view.planet, x, y)) {
          g.fillStyle(FARBE_AUSSEN, 0.62);
          g.fillRect(px, py, s, s);
        }
      }
    }
  }

  /**
   * Ein Vorkommen als Erzkörner — **je Ergiebigkeit mehr Körner**.
   *
   * Zwei Merkmale, nicht eines: die Farbe sagt, was es ist, die Zahl der
   * Körner, wie viel. Die Lage der Körner folgt aus der Kachel, sie bewegt
   * sich nie.
   */
  private drawDeposit(
    g: Phaser.GameObjects.Graphics, x: number, y: number, px: number, py: number, s: number,
  ): void {
    const vorkommen = depositAt(this.view.planet, x, y);
    if (!vorkommen) return;
    const hitze = vorkommen.kind === balancing().colony.depositHeat;
    const farbe = hitze ? 0xff7a2a : parseInt((goodById(vorkommen.kind)?.color ?? '#ffffff').slice(1), 16);
    const k = s / TILE;
    const koerner = 2 + vorkommen.rich * 2;
    for (let i = 0; i < koerner; i++) {
      const h = ((x * 92821) ^ (y * 68917) ^ (i * 40503)) >>> 0;
      const kx = 2 + (h % 11);
      const ky = 2 + ((h >>> 8) % 11);
      g.fillStyle(0x000000, 0.45);
      g.fillRect(px + (kx + 1) * k, py + (ky + 1) * k, 2 * k, 2 * k);
      g.fillStyle(farbe, 1);
      g.fillRect(px + kx * k, py + ky * k, 2 * k, 2 * k);
    }
    if (hitze) {
      g.lineStyle(Math.max(1, k), 0xffb04a, 0.8);
      g.strokeRect(px + 3 * k, py + 3 * k, s - 6 * k, s - 6 * k);
    }
  }

  /** Anlagen: Platte, Icon, Stufenleiste — und die Zeichen für Halt und Hunger. */
  private drawBuildings(g: Phaser.GameObjects.Graphics, outpost: Outpost): void {
    const s = TILE * this.zoom;
    const k = s / TILE;
    let n = 0;
    for (const anlage of outpost.buildings) {
      const kind = colonyBuildingById(anlage.kind);
      if (!kind) continue;
      const px = this.tileX(anlage.x);
      const py = this.tileY(anlage.y);
      g.fillStyle(PLATTE[kind.role] ?? 0x34465a, anlage.on ? 0.92 : 0.5);
      g.fillRect(px + k, py + k, s - 2 * k, s - 2 * k);
      this.placeIcon(n++, kind.icon, px + s / 2, py + s / 2, anlage.on);
      this.drawLevel(g, anlage, kind.maxLevel, px, py, s, k);
      if (!anlage.on) {
        g.fillStyle(0xffffff, 0.85);
        g.fillRect(px + 5 * k, py + 4 * k, 2 * k, 8 * k);
        g.fillRect(px + 9 * k, py + 4 * k, 2 * k, 8 * k);
      } else if (anlage.recipe && this.starved[anlage.recipe]) {
        g.fillStyle(FARBE_HUNGER, 1);
        g.fillRect(px + s - 4 * k, py + k, 3 * k, 3 * k);
      }
    }
    for (let i = n; i < this.icons.length; i++) this.icons[i].setVisible(false);
  }

  /** Die Leiste zeigt Stufe und Höchststufe zugleich — ohne Zahl, wie im Baum. */
  private drawLevel(
    g: Phaser.GameObjects.Graphics, anlage: ColonyBuildingState, max: number,
    px: number, py: number, s: number, k: number,
  ): void {
    const breite = (s - 4 * k) * Math.min(1, anlage.level / max);
    g.fillStyle(0x000000, 0.6);
    g.fillRect(px + 2 * k, py + s - 3 * k, s - 4 * k, 2 * k);
    g.fillStyle(FARBE_STUFE, 1);
    g.fillRect(px + 2 * k, py + s - 3 * k, Math.max(k, breite), 2 * k);
  }

  private placeIcon(index: number, name: string, x: number, y: number, on: boolean): void {
    while (this.icons.length <= index) {
      this.icons.push(this.add.image(0, 0, iconTextureKey('lock')).setDepth(1));
    }
    const key = iconTextureKey(name);
    this.icons[index]
      .setTexture(this.textures.exists(key) ? key : iconTextureKey('lock'))
      .setPosition(x, y)
      .setScale(ICON_SCALE * this.zoom)
      .setAlpha(on ? 1 : 0.55)
      .setVisible(true);
  }

  /** Auswahl, Bauplan-Ziele und die Kachel unter dem Zeiger. */
  private drawMarks(g: Phaser.GameObjects.Graphics, colony: ColonyState, outpost: Outpost): void {
    const s = TILE * this.zoom;
    const kind = this.view.placing ? colonyBuildingById(this.view.placing) : undefined;
    const bewegt = this.view.moving && this.view.selected
      ? buildingAt(outpost, this.view.selected.x, this.view.selected.y) : undefined;
    const bewegtArt = bewegt ? colonyBuildingById(bewegt.kind) : undefined;
    if (kind || bewegtArt) {
      const c = balancing().colony;
      g.lineStyle(Math.max(1, this.zoom), FARBE_FREI, 0.85);
      for (let y = 0; y < c.gridH; y++) {
        for (let x = 0; x < c.gridW; x++) {
          const frei = kind
            ? placeLocked(store.save, this.view.planet, kind, x, y) === ''
            : bewegtArt !== undefined && inArea(colony, this.view.planet, x, y)
              && !buildingAt(outpost, x, y) && siteOk(this.view.planet, bewegtArt, x, y);
          if (frei) g.strokeRect(this.tileX(x) + 1, this.tileY(y) + 1, s - 2, s - 2);
        }
      }
    }
    for (const ziel of [this.view.selected, this.hover]) {
      if (!ziel) continue;
      g.lineStyle(Math.max(1, this.zoom), FARBE_AUSWAHL, ziel === this.hover ? 0.45 : 1);
      g.strokeRect(this.tileX(ziel.x), this.tileY(ziel.y), s, s);
    }
  }

  /** Welche Kachel unter einem Zeiger liegt — oder `null` daneben. */
  private tileAt(pointer: Phaser.Input.Pointer): { x: number; y: number } | null {
    const camera = this.cameras.main;
    const c = balancing().colony;
    const x = Math.floor((pointer.x - camera.x - this.ox) / (TILE * this.zoom));
    const y = Math.floor((pointer.y - camera.y - this.oy) / (TILE * this.zoom));
    return x >= 0 && x < c.gridW && y >= 0 && y < c.gridH ? { x, y } : null;
  }

  private bindInput(): void {
    // **Treffer beim Drücken**, nicht beim Bewegen: ein Finger kennt kein
    // Zeigen (`oberflaeche.md`).
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.open || !store.save.colony.planets[this.view.planet]) return;
      const kachel = this.tileAt(pointer);
      if (kachel) this.callbacks.onTile(kachel.x, kachel.y);
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.open) return;
      this.hover = this.tileAt(pointer);
      this.label.setText(this.hover ? this.hoverText(this.hover.x, this.hover.y) : '');
    });
    bus.on('panel:close', () => {
      this.hover = null;
      this.label.setText('');
    });
  }

  /** Die Zeile oben links: was unter dem Zeiger liegt. */
  private hoverText(x: number, y: number): string {
    const outpost = store.save.colony.planets[this.view.planet];
    const anlage = outpost ? buildingAt(outpost, x, y) : undefined;
    if (anlage) {
      const kind = colonyBuildingById(anlage.kind);
      return `${kind?.name ?? anlage.kind} · Stufe ${anlage.level}`;
    }
    const vorkommen = depositAt(this.view.planet, x, y);
    if (!vorkommen) return 'Gestein';
    const c = balancing().colony;
    const name = vorkommen.kind === c.depositHeat ? c.depositHeatName : goodById(vorkommen.kind)?.name;
    return `${name ?? vorkommen.kind} · Ergiebigkeit ${vorkommen.rich}`;
  }
}
